import { type PropsWithChildren, useEffect, useRef } from 'react';

interface ModalProps extends PropsWithChildren {
  labelledBy: string;
  onClose: () => void;
  className?: string;
  role?: 'dialog' | 'alertdialog';
  /**
   * `false` keeps the dialog standing: Escape and a click on the backdrop do not close it.
   *
   * This cannot be done with `preventDefault()` on `cancel` alone. A `<dialog>`'s `cancel` event
   * is only dispatched while the page holds transient user activation (close-watcher rules,
   * Chromium issue 41484805), and a dialog raised by a background timer - an expiring token, say -
   * is exactly the case without it. Escape then closes the element and no handler ever ran.
   * Reopening on `close` is what actually holds it.
   */
  dismissable?: boolean;
}

export function Modal({ children, labelledBy, onClose, className = '', role = 'dialog', dismissable = true }: ModalProps) {
  const dialogRef = useRef<HTMLDialogElement>(null);
  const returnFocusRef = useRef<HTMLElement | null>(null);
  // Read inside native listeners that are registered once; a direct capture would go stale.
  const onCloseRef = useRef(onClose);
  useEffect(() => { onCloseRef.current = onClose; });

  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;
    returnFocusRef.current = document.activeElement instanceof HTMLElement ? document.activeElement : null;

    const open = () => {
      if (dialog.open) return;
      if (typeof dialog.showModal === 'function') dialog.showModal();
      else dialog.setAttribute('open', '');
    };
    open();

    // React never reflects `autoFocus` into the DOM - it calls focus() in the layout phase, which
    // is before this effect and before showModal(), and showModal() then reassigns focus to the
    // first focusable descendant regardless. So the caller marks the element and we focus it here.
    dialog.querySelector<HTMLElement>('[data-initial-focus]')?.focus();

    // `cancel` and `close` do not bubble, so React's onCancel/onClose never fire through its
    // delegated listeners. These are registered on the element itself.
    const handleCancel = (event: Event) => {
      event.preventDefault();
      if (dismissable) onCloseRef.current();
    };
    const handleClose = () => { if (!dismissable) open(); };
    dialog.addEventListener('cancel', handleCancel);
    dialog.addEventListener('close', handleClose);

    return () => {
      // Remove the reopen listener before closing, or unmounting fights itself.
      dialog.removeEventListener('cancel', handleCancel);
      dialog.removeEventListener('close', handleClose);
      if (dialog.open) {
        if (typeof dialog.close === 'function') dialog.close();
        else dialog.removeAttribute('open');
      }
      returnFocusRef.current?.focus();
    };
  }, [dismissable]);

  return <dialog
    ref={dialogRef}
    className={`dialog ${className}`.trim()}
    role={role}
    aria-labelledby={labelledBy}
    onMouseDown={(event) => {
      if (!dismissable) return;
      if (event.target !== event.currentTarget) return;
      const bounds = event.currentTarget.getBoundingClientRect();
      const inside = event.clientX >= bounds.left && event.clientX <= bounds.right && event.clientY >= bounds.top && event.clientY <= bounds.bottom;
      if (!inside) onClose();
    }}
  >{children}</dialog>;
}
